import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Users, DollarSign, Database, Radar, Receipt, Send, ShieldAlert,
  TrendingUp, TrendingDown, Activity, Server, AlertTriangle, Wallet, PieChart, Scale,
  KeyRound, Plus, Trash2, CheckCircle2, Power, ShieldCheck, FlaskConical, XCircle, RefreshCw, Info,
  Mail, Inbox, MessageSquareWarning, Eye, Megaphone, Newspaper, Pencil, X,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Badge, Spinner, Input, Button } from "@/components/kit";
import AdminLogs from "@/components/AdminLogs";

const rupiah = (n) => "Rp" + Math.round(n || 0).toLocaleString("id-ID");
const usd = (n) => "$" + Number(n || 0).toFixed(4);
const fmtDate = (s) => (s ? new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "-");

const TABS = [
  { id: "overview", label: "Overview", icon: Activity },
  { id: "finance", label: "Laba Rugi", icon: Scale },
  { id: "useranalytics", label: "Analisa User", icon: PieChart },
  { id: "cost", label: "Biaya & Pricing", icon: DollarSign },
  { id: "users", label: "Users", icon: Users },
  { id: "registrations", label: "Registrasi", icon: TrendingUp },
  { id: "billing", label: "Billing Log", icon: Receipt },
  { id: "scrapes", label: "Apify Log", icon: Radar },
  { id: "apilogs", label: "API Traffic", icon: Server },
  { id: "tokens", label: "Token Apify", icon: KeyRound },
  { id: "blasts", label: "Blast Log", icon: Send },
  { id: "emaillog", label: "Email Log", icon: Mail },
  { id: "gateway", label: "Payment Gateway", icon: Wallet },
  { id: "contacts", label: "Pesan", icon: Inbox },
  { id: "complaints", label: "Pengaduan", icon: MessageSquareWarning },
  { id: "banner", label: "Banner", icon: Megaphone },
  { id: "blog", label: "Blog", icon: Newspaper },
];

function KPI({ icon: Icon, label, value, sub, color = "sky" }) {
  const cmap = {
    sky: "bg-sky-50 text-sky-600", green: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600", amber: "bg-amber-50 text-amber-600",
  };
  return (
    <Card className="p-5">
      <div className={`w-10 h-10 rounded-xl grid place-items-center mb-3 ${cmap[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="font-display text-2xl font-extrabold">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </Card>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [apifyRuns, setApifyRuns] = useState(null);
  const [users, setUsers] = useState(null);
  const [regs, setRegs] = useState(null);
  const [billing, setBilling] = useState(null);
  const [scrapes, setScrapes] = useState(null);
  const [blasts, setBlasts] = useState(null);
  const [finance, setFinance] = useState(null);
  const [ua, setUa] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [emaillog, setEmaillog] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [complaints, setComplaints] = useState(null);
  const [userSearch, setUserSearch] = useState("");

  const loadUsers = useCallback((s = "") => {
    api.get("/admin/users", { params: { search: s } }).then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/apify-runs").then((r) => setApifyRuns(r.data)).catch(() => {});
    api.get("/admin/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    if (tab === "users" && !users) loadUsers();
    if (tab === "finance" && !finance) api.get("/admin/finance").then((r) => setFinance(r.data)).catch(() => {});
    if (tab === "useranalytics" && !ua) api.get("/admin/user-analytics").then((r) => setUa(r.data)).catch(() => {});
    if (tab === "registrations" && !regs) api.get("/admin/logs/registrations").then((r) => setRegs(r.data.items)).catch(() => {});
    if (tab === "billing" && !billing) api.get("/admin/logs/billing").then((r) => setBilling(r.data.items)).catch(() => {});
    if (tab === "blasts" && !blasts) api.get("/admin/logs/blasts").then((r) => setBlasts(r.data.items)).catch(() => {});
    if (tab === "tokens" && !tokens) api.get("/admin/apify-tokens").then((r) => setTokens(r.data.tokens)).catch(() => {});
    if (tab === "emaillog" && !emaillog) api.get("/admin/email-logs").then((r) => setEmaillog(r.data.items)).catch(() => {});
    if (tab === "contacts" && !contacts) api.get("/admin/contacts").then((r) => setContacts(r.data.items)).catch(() => {});
    if (tab === "complaints" && !complaints) api.get("/admin/complaints").then((r) => setComplaints(r.data.items)).catch(() => {});
  }, [tab, user, users, regs, billing, blasts, finance, ua, tokens, emaillog, contacts, complaints, loadUsers]);

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const avgResults = overview && overview.apify.total_calls > 0
    ? overview.apify.total_results / overview.apify.total_calls : 12;
  const realCostPerLead = (apifyRuns?.avg_cost_per_run_usd || 0.0273) / Math.max(avgResults, 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-900 grid place-items-center"><ShieldAlert className="w-5 h-5 text-sky-400" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">Admin Console</h1>
          <p className="text-slate-500 text-sm">Monitoring sistem, biaya API, dan manajemen pengguna.</p>
        </div>
      </div>

      {/* Cost alerts banner */}
      {alerts && (alerts.alerts.length > 0 ? (
        <div className="space-y-2">
          {alerts.alerts.map((a, i) => (
            <div key={i} data-testid="cost-alert" className={`flex items-start gap-3 rounded-xl p-4 ${a.level === "danger" ? "bg-rose-50 border border-rose-200" : "bg-amber-50 border border-amber-200"}`}>
              <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${a.level === "danger" ? "text-rose-600" : "text-amber-600"}`} />
              <div className={`text-sm ${a.level === "danger" ? "text-rose-800" : "text-amber-800"}`}>
                <p className="font-semibold">{a.title}</p><p>{a.message}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Keuangan sehat — laba Rp{Math.round(alerts.profit_idr || 0).toLocaleString("id-ID")}
          {alerts.margin_pct != null ? ` · margin ${alerts.margin_pct}%` : " · belum ada pendapatan"} (batas sehat {Math.round(alerts.min_margin_pct)}%)
        </div>
      ))}

      {/* Vertical sidebar layout (sidebar-inside-sidebar) */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <aside className="w-full lg:w-56 lg:shrink-0 lg:sticky lg:top-6">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible rounded-2xl bg-white border border-slate-100 p-2">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition w-full text-left ${tab === t.id ? "bg-sky-500 text-white shadow-sm shadow-sky-200" : "text-slate-600 hover:bg-slate-100"}`}>
                <t.icon className="w-4 h-4 shrink-0" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="flex-1 min-w-0 w-full space-y-5">

      {/* OVERVIEW */}
      {tab === "overview" && (!overview ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI icon={Users} label="Total User" value={overview.users.total} sub={`+${overview.users.new_7d} dalam 7 hari`} />
            <KPI icon={DollarSign} label="Pendapatan" value={rupiah(overview.revenue_idr)} sub={`${overview.paid_count} transaksi`} color="green" />
            <KPI icon={Database} label="Total Leads" value={overview.leads.total} sub={`${overview.leads.manual} manual`} color="violet" />
            <KPI icon={Server} label="Biaya Apify (bln ini)" value={usd(overview.apify.account_monthly_usd)} sub="dari akun Apify" color="amber" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-6">
              <h3 className="font-display font-bold mb-4">Distribusi Plan</h3>
              {Object.entries(overview.users.by_plan).map(([plan, n]) => (
                <div key={plan} className="flex items-center justify-between py-1.5">
                  <Badge color={plan === "agency" ? "violet" : plan === "pro" ? "sky" : plan === "starter" ? "green" : "slate"} className="capitalize">{plan}</Badge>
                  <div className="flex items-center gap-2 flex-1 ml-3">
                    <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${Math.min((n / Math.max(overview.users.total, 1)) * 100, 100)}%` }} /></div>
                    <span className="text-sm font-semibold w-8 text-right">{n}</span>
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-400 mt-3">Login Google: {overview.users.google} user</p>
            </Card>
            <Card className="p-6">
              <h3 className="font-display font-bold mb-4">Aktivitas Sistem</h3>
              <div className="space-y-3 text-sm">
                <Row label="WA Devices terhubung" value={overview.devices} />
                <Row label="Pesan WA terkirim" value={overview.blast_sent} />
                <Row label="Total panggilan scrape" value={overview.apify.total_calls} />
                <Row label="Total hasil di-scrape" value={overview.apify.total_results} />
              </div>
            </Card>
          </div>
        </div>
      ))}

      {/* LABA RUGI (P&L) */}
      {tab === "finance" && (!finance ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI icon={Wallet} label="Pendapatan (real)" value={rupiah(finance.revenue.total_idr)} sub={`${finance.revenue.paid_count} transaksi lunas`} color="green" />
            <KPI icon={Server} label="Beban Biaya API" value={rupiah(finance.costs.total_idr)} sub={`Apify ${usd(finance.costs.apify_usd)}`} color="amber" />
            <FinanceKPI value={finance.pnl.gross_profit_idr} margin={finance.pnl.margin_pct} />
            <KPI icon={TrendingUp} label="MRR (langganan aktif)" value={rupiah(finance.revenue.mrr_idr)} sub={`ARPU ${rupiah(finance.revenue.arpu_idr)}`} color="sky" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Breakdown */}
            <Card className="p-6">
              <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Scale className="w-4 h-4 text-sky-500" /> Ringkasan Laba Rugi</h3>
              <div className="space-y-2.5 text-sm">
                <PnlRow label="Pendapatan langganan" value={rupiah(finance.revenue.subscription_idr)} />
                <PnlRow label="Pendapatan add-on" value={rupiah(finance.revenue.addon_idr)} />
                <div className="border-t border-slate-100 my-2" />
                <PnlRow label="Total Pendapatan" value={rupiah(finance.revenue.total_idr)} bold color="text-emerald-600" />
                <PnlRow label="Biaya Apify (scraping)" value={`- ${rupiah(finance.costs.apify_idr)}`} color="text-rose-500" />
                <PnlRow label={`Biaya Email Brevo (${finance.costs.emails_sent || 0} email)`} value={`- ${rupiah(finance.costs.brevo_idr)}`} color="text-rose-500" />
                <PnlRow label={`Fee Midtrans (est. ${finance.currency.midtrans_fee_pct}%)`} value={`- ${rupiah(finance.costs.midtrans_fee_idr)}`} color="text-rose-500" />
                <div className="border-t border-slate-100 my-2" />
                <PnlRow label="Laba / Rugi Kotor" value={rupiah(finance.pnl.gross_profit_idr)} bold
                  color={finance.pnl.gross_profit_idr >= 0 ? "text-emerald-600" : "text-rose-600"} />
              </div>
              <p className="text-xs text-slate-400 mt-4">Kurs USD→IDR: Rp{finance.currency.usd_to_idr.toLocaleString("id-ID")}. Biaya Apify ditarik real dari akun Apify.</p>
            </Card>

            {/* Monthly revenue chart */}
            <Card className="p-6">
              <h3 className="font-display font-bold mb-4">Pendapatan per Bulan</h3>
              {finance.revenue.by_month.length === 0 ? (
                <div className="h-[200px] grid place-items-center text-slate-400 text-sm">Belum ada pendapatan tercatat.</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={finance.revenue.by_month}>
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => rupiah(v)} />
                    <Bar dataKey="idr" radius={[6, 6, 0, 0]} fill="#0ea5e9" />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="mt-4 pt-3 border-t border-slate-50">
                <p className="text-xs font-semibold text-slate-500 mb-2">Pendapatan per Plan</p>
                {Object.keys(finance.revenue.by_plan).length === 0 ? <p className="text-xs text-slate-400">Belum ada.</p> :
                  Object.entries(finance.revenue.by_plan).map(([plan, v]) => (
                    <div key={plan} className="flex justify-between text-sm py-1"><span className="capitalize text-slate-600">{plan}</span><span className="font-semibold">{rupiah(v)}</span></div>
                  ))}
              </div>
            </Card>
          </div>
        </div>
      ))}

      {/* ANALISA USER */}
      {tab === "useranalytics" && (!ua ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <div className="space-y-5">
          <Card className="p-4 bg-sky-50 border-sky-200">
            <p className="text-sm text-sky-800 flex items-center gap-2"><PieChart className="w-4 h-4" /> Analisa profitabilitas & pemakaian per user. <b>Net</b> = pendapatan user − biaya Apify yang ia konsumsi.</p>
          </Card>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI icon={Users} label="Total User" value={ua.totals.users} color="sky" />
            <KPI icon={Database} label="Total Leads (akumulatif)" value={ua.totals.leads} sub={`${ua.totals.scrape_results} hasil scrape`} color="violet" />
            <KPI icon={Server} label="Total Biaya API" value={rupiah(ua.totals.cost_idr)} sub={`${ua.totals.scrape_calls} panggilan`} color="amber" />
            <FinanceKPI value={ua.totals.net_idr} label="Net Akumulatif" sub={`Pendapatan ${rupiah(ua.totals.revenue_idr)}`} />
          </div>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-3">User</th><th className="p-3">Plan</th>
                  <th className="p-3 text-right">Leads</th><th className="p-3 text-right">Scrape Call</th>
                  <th className="p-3 text-right">Hasil</th><th className="p-3 text-right">Email</th><th className="p-3 text-right">Biaya API</th>
                  <th className="p-3 text-right">Pendapatan</th><th className="p-3 text-right">Net</th><th className="p-3 text-right">WA Terkirim</th>
                </tr>
              </thead>
              <tbody>
                {ua.rows.length === 0 ? <tr><td colSpan={10} className="p-8 text-center text-slate-400">Belum ada data user.</td></tr> :
                  ua.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-50" data-testid="user-analytics-row">
                      <td className="p-3"><div className="font-medium">{r.name}</div><div className="text-xs text-slate-400">{r.email}</div></td>
                      <td className="p-3"><Badge color="sky" className="capitalize">{r.plan}</Badge></td>
                      <td className="p-3 text-right">{r.leads}</td>
                      <td className="p-3 text-right">{r.scrape_calls}</td>
                      <td className="p-3 text-right">{r.scrape_results}</td>
                      <td className="p-3 text-right">{r.emails_sent || 0}</td>
                      <td className="p-3 text-right text-rose-500">{rupiah(r.cost_idr)}</td>
                      <td className="p-3 text-right text-emerald-600">{rupiah(r.revenue_idr)}</td>
                      <td className={`p-3 text-right font-semibold ${r.net_idr >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{rupiah(r.net_idr)}</td>
                      <td className="p-3 text-right">{r.blast_sent}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Card>
        </div>
      ))}

      {/* COST & PRICING */}
      {tab === "cost" && (!overview ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <div className="space-y-5">
          <Card className="p-5 bg-amber-50 border-amber-200">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Biaya Apify nyata bulan ini: {usd(overview.apify.account_monthly_usd)}</p>
                <p className="mt-1">Optimasi sudah diterapkan: review, foto & pertanyaan di-skip saat scrape (hanya ambil data inti + telepon) untuk menekan biaya per panggilan.</p>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI icon={DollarSign} label="Total biaya (real)" value={usd(apifyRuns?.total_cost_usd)} sub={`${apifyRuns?.count || 0} runs`} color="amber" />
            <KPI icon={Activity} label="Rata2 / panggilan (real)" value={usd(apifyRuns?.avg_cost_per_run_usd)} color="sky" />
            <KPI icon={Radar} label="Biaya / 1000 hasil (real)" value={usd(realCostPerLead * 1000)} sub="dari data nyata" color="violet" />
            <KPI icon={TrendingUp} label="Biaya / hasil (real)" value={usd(realCostPerLead)} sub={`~${avgResults.toFixed(0)} hasil/panggilan`} color="green" />
          </div>

          {/* Pricing recommendation */}
          <Card className="p-6">
            <h3 className="font-display font-bold mb-1">Rekomendasi Pricing (margin sehat)</h3>
            <p className="text-sm text-slate-500 mb-4">Berdasarkan biaya Apify NYATA ~{usd(realCostPerLead)}/lead (≈ Rp{Math.round(realCostPerLead * 16000).toLocaleString("id-ID")}/lead), diukur dari {apifyRuns?.count || 0} panggilan terakhir.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-500 text-left"><tr><th className="py-2">Plan</th><th>Quota Leads</th><th>Biaya Apify/bln*</th><th>Harga</th><th>Est. Margin</th></tr></thead>
                <tbody>
                  {[
                    { p: "Starter", leads: 1000, price: 79000 },
                    { p: "Pro", leads: 5000, price: 199000 },
                    { p: "Agency", leads: 20000, price: 499000 },
                  ].map((r) => {
                    const costUsd = r.leads * realCostPerLead;
                    const costIdr = Math.round(costUsd * 16000);
                    const margin = Math.round(((r.price - costIdr) / r.price) * 100);
                    return (
                      <tr key={r.p} className="border-t border-slate-50">
                        <td className="py-2 font-semibold">{r.p}</td>
                        <td>{r.leads.toLocaleString("id-ID")}</td>
                        <td>{usd(costUsd)} (≈Rp{costIdr.toLocaleString("id-ID")})</td>
                        <td>{rupiah(r.price)}</td>
                        <td><Badge color={margin > 60 ? "green" : margin > 30 ? "amber" : "red"}>{margin}%</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-3">*Asumsi user memakai 100% quota leads-nya. Margin nyata biasanya lebih tinggi karena rata-rata pemakaian {"<"} 50% & banyak hasil duplikat tidak ditagih ulang.</p>
          </Card>

          {/* Real runs */}
          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-slate-100"><h3 className="font-display font-bold">Riwayat Panggilan Apify (biaya real per call)</h3></div>
            {!apifyRuns ? <Spinner className="w-6 h-6 mx-auto my-8" /> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="p-3">Waktu</th><th className="p-3">Status</th><th className="p-3 text-right">Biaya (USD)</th></tr></thead>
                <tbody>
                  {apifyRuns.runs.map((r) => (
                    <tr key={r.id} className="border-t border-slate-50">
                      <td className="p-3">{fmtDate(r.started_at)}</td>
                      <td className="p-3"><Badge color={r.status === "SUCCEEDED" ? "green" : "amber"}>{r.status}</Badge></td>
                      <td className="p-3 text-right font-semibold">{usd(r.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      ))}

      {/* USERS */}
      {tab === "users" && (
        <div className="space-y-4">
          <div className="relative max-w-sm">
            <Input data-testid="admin-user-search" placeholder="Cari nama / email..." value={userSearch}
              onChange={(e) => { setUserSearch(e.target.value); loadUsers(e.target.value); }} />
          </div>
          {!users ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">Plan</th><th className="p-3">Login</th><th className="p-3">Leads</th><th className="p-3">Daftar</th></tr></thead>
                <tbody>
                  {users.users.map((u) => (
                    <tr key={u.id} className="border-t border-slate-50" data-testid="admin-user-row">
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3 text-slate-500">{u.email}</td>
                      <td className="p-3"><Badge color="sky" className="capitalize">{u.plan}</Badge></td>
                      <td className="p-3"><Badge color="slate">{u.auth_provider || "email"}</Badge></td>
                      <td className="p-3">{u.lead_count}</td>
                      <td className="p-3 text-xs text-slate-400">{fmtDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {/* REGISTRATIONS */}
      {tab === "registrations" && (!regs ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <LogTable cols={["Nama", "Email", "Plan", "Login", "Verifikasi", "Waktu"]} rows={regs.map((r) => [
          r.name, r.email, <Badge color="sky" className="capitalize">{r.plan}</Badge>, <Badge color="slate">{r.provider}</Badge>,
          <span>{r.email_verified ? "✉️" : "·"} {r.phone_verified ? "📱" : "·"}</span>, fmtDate(r.created_at),
        ])} />
      ))}

      {/* BILLING */}
      {tab === "billing" && (!billing ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <LogTable cols={["Order ID", "Tipe", "Jumlah", "Status", "Waktu"]} rows={billing.map((b) => [
          <span className="font-mono text-xs">{b.order_id}</span>, <span className="capitalize">{b.type}</span>, rupiah(b.gross_amount),
          <Badge color={b.paid ? "green" : "amber"}>{b.status}</Badge>, fmtDate(b.created_at),
        ])} />
      ))}

      {/* APIFY LOG — real usage straight from Apify */}
      {tab === "scrapes" && (!apifyRuns ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <div className="space-y-4">
          <Card className="p-4 bg-sky-50 border-sky-200">
            <div className="flex gap-3">
              <Server className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
              <p className="text-sm text-sky-800">Data di bawah ditarik <b>langsung dari API Apify</b> (akun kamu) — biaya & status nyata per panggilan, bukan estimasi internal.</p>
            </div>
          </Card>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI icon={Server} label="Usage Apify (bln ini)" value={usd(apifyRuns.account_monthly_usd)} sub="dari akun Apify" color="amber" />
            <KPI icon={Radar} label="Total panggilan" value={apifyRuns.count} sub={`${apifyRuns.succeeded} sukses`} color="sky" />
            <KPI icon={DollarSign} label="Total biaya (runs)" value={usd(apifyRuns.total_cost_usd)} color="green" />
            <KPI icon={Activity} label="Rata2 / panggilan" value={usd(apifyRuns.avg_cost_per_run_usd)} color="violet" />
          </div>
          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-bold">Riwayat Run Apify (real-time)</h3>
              <button onClick={() => api.get("/admin/apify-runs").then((r) => setApifyRuns(r.data))} className="text-sm text-sky-600 font-semibold hover:underline">Refresh</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr><th className="p-3">Mulai</th><th className="p-3">Status</th><th className="p-3 text-right">Durasi</th><th className="p-3 text-right">Compute Units</th><th className="p-3 text-right">Biaya (USD)</th><th className="p-3">Run ID</th></tr>
                </thead>
                <tbody>
                  {apifyRuns.runs.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">Belum ada run Apify.</td></tr> :
                    apifyRuns.runs.map((r) => (
                      <tr key={r.id} className="border-t border-slate-50" data-testid="apify-run-row">
                        <td className="p-3 whitespace-nowrap">{fmtDate(r.started_at)}</td>
                        <td className="p-3"><Badge color={r.status === "SUCCEEDED" ? "green" : r.status === "FAILED" ? "red" : "amber"}>{r.status}</Badge></td>
                        <td className="p-3 text-right">{r.duration_secs != null ? `${r.duration_secs}s` : "-"}</td>
                        <td className="p-3 text-right">{r.compute_units || "-"}</td>
                        <td className="p-3 text-right font-semibold">{usd(r.cost_usd)}</td>
                        <td className="p-3"><a href={r.console_url} target="_blank" rel="noreferrer" className="text-sky-600 font-mono text-xs hover:underline">{r.id?.slice(0, 8)}…</a></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      ))}

      {/* API TRAFFIC LOGS */}
      {tab === "apilogs" && <AdminLogs />}

      {/* TOKEN APIFY */}
      {tab === "tokens" && <TokensPanel tokens={tokens} setTokens={setTokens} />}

      {/* BLASTS */}
      {tab === "blasts" && (!blasts ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <LogTable cols={["Tipe", "Total", "Terkirim", "Gagal", "Status", "Waktu"]} rows={blasts.map((b) => [
          (b.blast_type || "wa").toUpperCase(), b.total_leads, b.sent, b.failed, <Badge color="sky">{b.status}</Badge>, fmtDate(b.created_at),
        ])} />
      ))}

      {/* EMAIL LOG */}
      {tab === "emaillog" && (!emaillog ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        <LogTable cols={["User", "Tujuan", "Status", "Preview", "Waktu"]} rows={emaillog.map((e) => [
          <span className="text-xs">{e.user_email}</span>, e.target_number,
          <Badge color={e.status === "sent" ? "green" : e.status === "failed" ? "red" : "slate"}>{e.status}</Badge>,
          <span className="text-xs text-slate-400 max-w-[240px] truncate inline-block">{e.message_preview || "—"}</span>,
          fmtDate(e.sent_at),
        ])} />
      ))}

      {/* CONTACTS */}
      {tab === "contacts" && (!contacts ? <Spinner className="w-7 h-7 mx-auto my-20" /> : (
        contacts.length === 0 ? <Card className="p-10 text-center text-slate-400">Belum ada pesan masuk.</Card> : (
          <div className="space-y-3" data-testid="admin-contacts">
            {contacts.map((c) => (
              <Card key={c.id} className="p-4" data-testid="contact-row">
                <div className="flex items-center justify-between">
                  <div><span className="font-semibold text-slate-800">{c.name}</span> <span className="text-xs text-slate-400">· {c.email}</span></div>
                  <div className="flex items-center gap-2">
                    {c.status === "new" && <Badge color="amber">Baru</Badge>}
                    <span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-2">{c.message}</p>
                <a href={`mailto:${c.email}`} className="text-xs text-sky-600 font-semibold mt-2 inline-block">Balas via email →</a>
              </Card>
            ))}
          </div>
        )
      ))}

      {/* COMPLAINTS */}
      {tab === "complaints" && <ComplaintsPanel complaints={complaints} setComplaints={setComplaints} />}

      {/* BANNER */}
      {tab === "banner" && <BannerPanel />}
      {tab === "blog" && <BlogPanel />}
      {tab === "gateway" && <PaymentGatewayPanel />}
      </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span className="font-semibold">{value}</span></div>;
}

function PnlRow({ label, value, bold, color = "text-slate-800" }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${bold ? "font-semibold text-slate-700" : "text-slate-500"}`}>{label}</span>
      <span className={`${bold ? "font-extrabold" : "font-semibold"} ${color}`}>{value}</span>
    </div>
  );
}

function FinanceKPI({ value, margin, label = "Laba / Rugi", sub }) {
  const positive = (value || 0) >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <Card className="p-5">
      <div className={`w-10 h-10 rounded-xl grid place-items-center mb-3 ${positive ? "bg-emerald-50" : "bg-rose-50"}`}>
        <Icon className={`w-5 h-5 ${positive ? "text-emerald-600" : "text-rose-600"}`} />
      </div>
      <p className={`font-display text-2xl font-extrabold ${positive ? "text-emerald-600" : "text-rose-600"}`}>
        {"Rp" + Math.round(value || 0).toLocaleString("id-ID")}
      </p>
      <p className="text-sm text-slate-500 mt-0.5">{label}{margin != null ? ` · margin ${margin}%` : ""}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </Card>
  );
}

function LogTable({ cols, rows }) {
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-left"><tr>{cols.map((c) => <th key={c} className="p-3 whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={cols.length} className="p-8 text-center text-slate-400">Belum ada data.</td></tr> :
            rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-50" data-testid="admin-log-row">
                {r.map((cell, j) => <td key={j} className="p-3 whitespace-nowrap">{cell}</td>)}
              </tr>
            ))}
        </tbody>
      </table>
    </Card>
  );
}

const TOKEN_STATUS = { ok: "green", error: "red", unknown: "slate" };

function PaymentGatewayPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState("");
  const load = () => api.get("/admin/payment-gateway").then((r) => setCfg(r.data)).catch(() => toast.error("Gagal memuat"));
  useEffect(() => { load(); }, []);

  const switchTo = async (gateway) => {
    if (!cfg || cfg.gateway === gateway) return;
    setSaving(gateway);
    try {
      const { data } = await api.post("/admin/payment-gateway", { gateway });
      setCfg((c) => ({ ...c, gateway: data.gateway }));
      toast.success(`Gateway aktif: ${data.gateway === "ipaymu" ? "iPaymu" : "Midtrans"}`);
    } catch { toast.error("Gagal mengubah gateway"); }
    finally { setSaving(""); }
  };

  if (!cfg) return <Spinner className="w-7 h-7 mx-auto my-16" />;

  const OPTIONS = [
    { id: "midtrans", name: "Midtrans", desc: "Snap popup — kartu, e-wallet, VA, QRIS.", info: cfg.midtrans },
    { id: "ipaymu", name: "iPaymu", desc: "Redirect payment — VA, e-wallet, QRIS, retail.", info: cfg.ipaymu },
  ];

  return (
    <div className="space-y-5 max-w-3xl" data-testid="payment-gateway-panel">
      <Card className="p-4 bg-sky-50 border-sky-200">
        <p className="text-sm text-sky-900 flex items-center gap-2">
          <Wallet className="w-4 h-4" /> Pilih payment gateway aktif. Berlaku <b>global</b> untuk semua transaksi (subscription, add-on, top-up kredit).
        </p>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        {OPTIONS.map((o) => {
          const active = cfg.gateway === o.id;
          return (
            <Card key={o.id} className={`p-5 border-2 transition ${active ? "border-sky-400 bg-sky-50/40" : "border-slate-200"}`} data-testid={`gateway-card-${o.id}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-bold">{o.name}</h3>
                {active ? <Badge color="green"><CheckCircle2 className="w-3 h-3" /> Aktif</Badge> : <Badge color="slate">Nonaktif</Badge>}
              </div>
              <p className="text-sm text-slate-500 mt-1">{o.desc}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge color={o.info?.configured ? "green" : "red"}>{o.info?.configured ? "Terkonfigurasi" : "Belum ada key"}</Badge>
                <Badge color={o.info?.sandbox ? "amber" : "sky"}>{o.info?.sandbox ? "Sandbox" : "Production"}</Badge>
              </div>
              {o.id === "ipaymu" && o.info?.va && <p className="text-xs text-slate-400 mt-2 font-mono">VA: {o.info.va}</p>}
              <Button data-testid={`gateway-activate-${o.id}`} className="w-full mt-4" variant={active ? "outline" : "primary"}
                disabled={active || !o.info?.configured} loading={saving === o.id} onClick={() => switchTo(o.id)}>
                <Power className="w-4 h-4" /> {active ? "Sedang Aktif" : "Aktifkan"}
              </Button>
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-slate-400">
        Kredensial gateway diatur lewat environment variable backend (MIDTRANS_*, IPAYMU_VA, IPAYMU_API_KEY, IPAYMU_IS_SANDBOX). Toggle ini hanya memilih gateway mana yang dipakai.
      </p>
    </div>
  );
}

function BannerPanel() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/banner").then((r) => setB(r.data)).catch(() => setB({ enabled: false, text: "", image_url: "", link: "" })); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.post("/admin/banner", b); toast.success("Banner disimpan"); }
    catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };
  if (!b) return <Spinner className="w-7 h-7 mx-auto my-16" />;
  return (
    <div className="space-y-5 max-w-2xl" data-testid="banner-panel">
      <Card className="p-4 bg-sky-50 border-sky-200"><p className="text-sm text-sky-800">Banner advertorial tampil di atas setiap halaman pengguna. Isi teks dan/atau URL gambar.</p></Card>
      <Card className="p-5 space-y-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" data-testid="banner-enabled" checked={b.enabled} onChange={(e) => setB({ ...b, enabled: e.target.checked })} /> Aktifkan banner
        </label>
        <div><label className="text-xs font-semibold text-slate-500">Teks</label>
          <Input data-testid="banner-text" value={b.text || ""} onChange={(e) => setB({ ...b, text: e.target.value })} placeholder="Promo spesial bulan ini..." className="mt-1" /></div>
        <div><label className="text-xs font-semibold text-slate-500">URL Gambar (opsional)</label>
          <Input data-testid="banner-image" value={b.image_url || ""} onChange={(e) => setB({ ...b, image_url: e.target.value })} placeholder="https://..." className="mt-1" /></div>
        <div><label className="text-xs font-semibold text-slate-500">Link tujuan (opsional)</label>
          <Input data-testid="banner-link" value={b.link || ""} onChange={(e) => setB({ ...b, link: e.target.value })} placeholder="https://..." className="mt-1" /></div>
        <Button data-testid="banner-save" onClick={save} loading={saving}>Simpan Banner</Button>
      </Card>
    </div>
  );
}

function ComplaintsPanel({ complaints, setComplaints }) {
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const resolve = async (id, status) => {
    try {
      await api.post(`/admin/complaints/${id}/resolve`, { status, reply });
      toast.success("Pengaduan diperbarui");
      setComplaints((cs) => cs.map((c) => (c.id === id ? { ...c, status, admin_reply: reply } : c)));
      setActive(null); setReply("");
    } catch { toast.error("Gagal memperbarui"); }
  };

  if (!complaints) return <Spinner className="w-7 h-7 mx-auto my-20" />;
  if (complaints.length === 0) return <Card className="p-10 text-center text-slate-400">Belum ada pengaduan.</Card>;
  return (
    <div className="space-y-3" data-testid="admin-complaints">
      {complaints.map((c) => (
        <Card key={c.id} className="p-4" data-testid="complaint-row">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-800">{c.subject}</span>
                <Badge color="slate">{c.category}</Badge>
                <Badge color={c.status === "open" ? "amber" : "green"}>{c.status}</Badge>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{c.user_name} · {c.user_email} · {fmtDate(c.created_at)}</p>
              <p className="text-sm text-slate-600 mt-2">{c.message}</p>
              {c.admin_reply && <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg p-2 mt-2"><b>Balasan:</b> {c.admin_reply}</p>}
            </div>
            {c.image_base64 && (
              <button onClick={() => setActive({ ...c, view: "img" })} data-testid="complaint-image-btn"
                className="shrink-0 relative group">
                <img src={c.image_base64} alt="bukti" className="h-16 w-16 object-cover rounded-lg border border-slate-200" />
                <span className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 rounded-lg grid place-items-center transition">
                  <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
                </span>
              </button>
            )}
          </div>
          {c.status === "open" && (
            <div className="mt-3">
              <button onClick={() => { setActive(active?.id === c.id && active.view === "reply" ? null : { ...c, view: "reply" }); setReply(c.admin_reply || ""); }}
                data-testid="complaint-reply-btn" className="text-sm text-sky-600 font-semibold">Tanggapi / Selesaikan →</button>
              {active?.id === c.id && active.view === "reply" && (
                <div className="mt-2 space-y-2">
                  <textarea data-testid="complaint-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Balasan untuk user..."
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" />
                  <div className="flex gap-2">
                    <Button size="sm" data-testid="complaint-resolve" onClick={() => resolve(c.id, "resolved")}>Tandai Selesai</Button>
                    <Button size="sm" variant="outline" onClick={() => resolve(c.id, "closed")}>Tutup</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      ))}

      {active?.view === "img" && (
        <div className="fixed inset-0 z-50 grid place-items-center p-6" onClick={() => setActive(null)}>
          <div className="absolute inset-0 bg-slate-900/70" />
          <img src={active.image_base64} alt="bukti" className="relative max-h-[85vh] max-w-full rounded-xl" data-testid="complaint-image-full" />
        </div>
      )}
    </div>
  );
}

function TokensPanel({ tokens, setTokens }) {
  const [label, setLabel] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState({});
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageAt, setUsageAt] = useState(null);
  const [testingAll, setTestingAll] = useState(false);
  const [testing, setTesting] = useState({}); // {token_id: true}
  const [testResults, setTestResults] = useState({}); // {token_id: {valid, error|username, at}}

  const fetchUsage = useCallback(async (showSpinner = false) => {
    if (showSpinner) setUsageLoading(true);
    try {
      const { data } = await api.get("/admin/apify-tokens/usage");
      setUsage(data.usage || {});
      setUsageAt(Date.now());
    } catch { /* silent — UI sudah punya usageAt indicator */ }
    finally { if (showSpinner) setUsageLoading(false); }
  }, []);

  // Initial + refetch when token list changes (add / activate / toggle / delete).
  useEffect(() => { fetchUsage(false); }, [tokens, fetchUsage]);

  // Auto-poll every 30s so usage stays live after scrape calls.
  useEffect(() => {
    const id = setInterval(() => fetchUsage(false), 30000);
    return () => clearInterval(id);
  }, [fetchUsage]);

  // Refetch immediately when the admin tab regains focus.
  useEffect(() => {
    const onFocus = () => fetchUsage(false);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchUsage]);

  const add = async () => {
    if (token.trim().length < 10) { toast.error("Token Apify tidak valid"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/apify-tokens", { label: label || "Token", token: token.trim() });
      setTokens(data.tokens);
      setLabel(""); setToken("");
      if (data.verify?.valid) toast.success(`Token ditambahkan & valid (akun: ${data.verify.username || "ok"})`);
      else toast.warning(`Token ditambahkan tapi gagal diverifikasi: ${data.verify?.error || "?"}`);
    } catch (e) { toast.error("Gagal menambah token"); }
    finally { setBusy(false); }
  };

  const act = async (id, path, method = "post") => {
    try {
      const { data } = await api[method](`/admin/apify-tokens/${id}${path}`);
      setTokens(data.tokens);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aksi gagal"); }
  };

  const testOne = async (id) => {
    setTesting((s) => ({ ...s, [id]: true }));
    try {
      const { data } = await api.post(`/admin/apify-tokens/${id}/test`);
      setTokens(data.tokens);
      setTestResults((r) => ({ ...r, [id]: { ...data.result, at: Date.now() } }));
      if (data.result.valid) toast.success(`Token valid (akun: ${data.result.username || "ok"})`);
      else toast.error(`Token gagal: ${data.result.error || "?"}`);
    } catch (e) { toast.error("Tes gagal"); }
    finally { setTesting((s) => ({ ...s, [id]: false })); }
  };

  const testAll = async () => {
    setTestingAll(true);
    try {
      const { data } = await api.post("/admin/apify-tokens/test-all");
      setTokens(data.tokens);
      const map = {};
      (data.results || []).forEach((r) => { map[r.id] = { ...r, at: Date.now() }; });
      setTestResults((prev) => ({ ...prev, ...map }));
      const { valid, invalid, total } = data.summary || {};
      if (invalid > 0) toast.warning(`${valid}/${total} valid · ${invalid} gagal — lihat kolom "Tes Token"`);
      else toast.success(`Semua ${total} token valid ✓`);
    } catch (e) { toast.error("Bulk test gagal"); }
    finally { setTestingAll(false); }
  };

  return (
    <div className="space-y-5" data-testid="tokens-panel">
      <Card className="p-4 bg-sky-50 border-sky-200">
        <p className="text-sm text-sky-800 flex items-center gap-2"><ShieldCheck className="w-4 h-4 shrink-0" /> Simpan beberapa token Apify sebagai cadangan. Saat scraping, sistem otomatis mencoba token <b>aktif</b> dulu, lalu <b>failover</b> ke token lain bila gagal/limit.</p>
      </Card>

      <Card className="p-5">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-sky-500" /> Tambah Token Apify</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <Input data-testid="token-label" placeholder="Label (mis. Akun Backup 1)" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Input data-testid="token-value" placeholder="apify_api_xxxxxxxx" value={token} onChange={(e) => setToken(e.target.value)} className="md:col-span-1" />
          <Button data-testid="token-add" onClick={add} loading={busy}><Plus className="w-4 h-4" /> Tambah & Verifikasi</Button>
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          {tokens?.length || 0} token tersimpan{Object.keys(testResults).length > 0 && (
            <span className="ml-2">· tes terakhir: <b className="text-emerald-600">{Object.values(testResults).filter((r) => r.valid).length} valid</b> · <b className="text-rose-500">{Object.values(testResults).filter((r) => !r.valid).length} gagal</b></span>
          )}
          {usageAt && <span className="ml-2 text-xs text-slate-400">· usage diperbarui {new Date(usageAt).toLocaleTimeString("id-ID")}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="token-refresh-usage" onClick={() => fetchUsage(true)} loading={usageLoading} variant="outline" size="sm" title="Tarik ulang usage dari API Apify">
            <RefreshCw className="w-4 h-4 text-sky-500" /> Refresh Usage
          </Button>
          <Button data-testid="token-test-all" onClick={testAll} loading={testingAll} variant="outline" disabled={!tokens || tokens.length === 0}>
            <FlaskConical className="w-4 h-4 text-violet-500" /> Tes Semua Token
          </Button>
        </div>
      </div>

      <Card className="p-3 bg-amber-50 border-amber-200 text-xs text-amber-800 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Angka usage di-cache Apify dan biasanya update <b>1–3 menit</b> setelah panggilan API berhasil.
          Halaman ini otomatis refresh tiap 30 detik. Buka tab <b>&ldquo;Apify Log&rdquo;</b> untuk lihat panggilan real-time per call.
        </span>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>
            <th className="p-3">Label</th><th className="p-3">Token</th><th className="p-3">Usage / Sisa</th><th className="p-3">Status</th><th className="p-3">Tes Token</th><th className="p-3">Aktif</th><th className="p-3 text-right">Aksi</th>
          </tr></thead>
          <tbody>
            {!tokens ? <tr><td colSpan={7} className="p-8 text-center"><Spinner className="w-6 h-6 mx-auto" /></td></tr> :
              tokens.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">Belum ada token. Sistem memakai token bawaan .env.</td></tr> :
                tokens.map((t) => {
                  const u = usage[t.id] || {};
                  const tr = testResults[t.id];
                  return (
                  <tr key={t.id} className="border-t border-slate-50" data-testid="token-row">
                    <td className="p-3 font-medium">{t.label}{t.source === "env" && <Badge color="slate" className="ml-2">bawaan</Badge>}</td>
                    <td className="p-3 font-mono text-xs text-slate-500">{t.masked}</td>
                    <td className="p-3 text-xs" data-testid="token-usage">
                      {u.error ? <span className="text-rose-400">{u.error}</span> :
                        u.usage_usd != null ? (
                          <span>
                            <span className="text-slate-600">${u.usage_usd}</span>
                            {u.limit_usd != null && <span className="text-slate-400"> / ${u.limit_usd}</span>}
                            {u.remaining_usd != null && <span className="block text-emerald-600">sisa ${u.remaining_usd}</span>}
                          </span>
                        ) : <Spinner className="w-3 h-3" />}
                    </td>
                    <td className="p-3">
                      <Badge color={TOKEN_STATUS[t.status] || "slate"}>{t.status === "ok" ? "OK" : t.status === "error" ? "Error" : "Belum dipakai"}</Badge>
                      {t.last_error && <span className="block text-[11px] text-rose-400 mt-1 max-w-[200px] truncate" title={t.last_error}>{t.last_error}</span>}
                    </td>
                    <td className="p-3 text-xs" data-testid="token-test-cell">
                      {tr ? (
                        tr.valid ? (
                          <Badge color="green"><CheckCircle2 className="w-3 h-3" /> Valid{tr.username ? ` · ${tr.username}` : ""}</Badge>
                        ) : (
                          <span className="text-rose-500 inline-flex items-start gap-1">
                            <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span className="max-w-[180px] truncate" title={tr.error}>{tr.error || "Gagal"}</span>
                          </span>
                        )
                      ) : <span className="text-slate-300">Belum dites</span>}
                    </td>
                    <td className="p-3">{t.active ? <Badge color="green"><CheckCircle2 className="w-3 h-3" /> Aktif</Badge> : <span className="text-slate-300">—</span>}</td>
                    <td className="p-3 text-right whitespace-nowrap space-x-1">
                      <button data-testid="token-test-single" onClick={() => testOne(t.id)} disabled={testing[t.id]}
                        className="text-violet-600 hover:bg-violet-50 rounded-lg p-1.5 disabled:opacity-50" title="Tes token ini">
                        {testing[t.id] ? <Spinner className="w-4 h-4" /> : <FlaskConical className="w-4 h-4" />}
                      </button>
                      {t.source !== "env" ? (
                        <>
                          {!t.active && <button data-testid="token-activate" onClick={() => act(t.id, "/activate")} className="text-emerald-600 hover:bg-emerald-50 rounded-lg p-1.5" title="Pakai token ini"><CheckCircle2 className="w-4 h-4" /></button>}
                          <button data-testid="token-toggle" onClick={() => act(t.id, "/toggle")} className={`rounded-lg p-1.5 ${t.enabled ? "text-slate-500 hover:bg-slate-100" : "text-amber-500 hover:bg-amber-50"}`} title={t.enabled ? "Nonaktifkan" : "Aktifkan"}><Power className="w-4 h-4" /></button>
                          <button data-testid="token-delete" onClick={() => act(t.id, "", "delete")} className="text-rose-500 hover:bg-rose-50 rounded-lg p-1.5" title="Hapus"><Trash2 className="w-4 h-4" /></button>
                        </>
                      ) : (
                        !t.active && <button data-testid="token-activate-env" onClick={() => act(t.id, "/activate")} className="text-emerald-600 hover:bg-emerald-50 rounded-lg p-1.5" title="Pakai token bawaan ini"><CheckCircle2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                  );
                })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const BLOG_CATS = ["Lead Generation", "Sales Automation", "WhatsApp Marketing", "Email Marketing", "AI untuk Sales", "Tips Closing", "Studi Kasus UMKM", "Umum"];
const EMPTY_POST = { title: "", excerpt: "", content: "", cover_url: "", category: "Umum", author: "Tim RESADA", published: true };

function BlogPanel() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/blog").then((r) => setPosts(r.data.posts)).catch(() => setPosts([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_POST); setEditing("new"); };
  const openEdit = async (p) => {
    try { const { data } = await api.get(`/blog/${p.slug}`); setForm({ ...data.post }); setEditing(p.id); }
    catch { toast.error("Gagal memuat artikel"); }
  };
  const save = async () => {
    if (!form.title || !form.excerpt || !form.content) { toast.error("Judul, excerpt & konten wajib diisi"); return; }
    if (form.excerpt.trim().length < 3) { toast.error("Excerpt minimal 3 karakter"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/blog", form);
      else await api.put(`/admin/blog/${editing}`, form);
      toast.success("Artikel disimpan"); setEditing(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(", ") : "Gagal menyimpan");
    } finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus artikel ini?")) return;
    try { await api.delete(`/admin/blog/${id}`); toast.success("Artikel dihapus"); load(); } catch { toast.error("Gagal hapus"); }
  };

  return (
    <Card className="p-6" data-testid="admin-blog-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display font-bold text-lg">Kelola Blog</h3>
          <p className="text-sm text-slate-500">{posts ? `${posts.length} artikel` : "Memuat..."}</p>
        </div>
        <Button onClick={openNew} data-testid="blog-new-btn"><Plus className="w-4 h-4" /> Artikel Baru</Button>
      </div>

      {!posts ? <Spinner /> : posts.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Belum ada artikel.</p> : (
        <div className="divide-y divide-slate-50">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-3 py-3" data-testid="admin-blog-row">
              <img src={p.cover_url} alt="" className="w-14 h-10 rounded-lg object-cover bg-slate-100" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 truncate">{p.title}</p>
                <p className="text-xs text-slate-400">{p.category} · {p.read_time} mnt · {p.published ? "Publish" : "Draft"}</p>
              </div>
              <button onClick={() => openEdit(p)} className="text-slate-500 hover:bg-slate-100 rounded-lg p-1.5" title="Edit"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(p.id)} className="text-rose-500 hover:bg-rose-50 rounded-lg p-1.5" title="Hapus"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/50" data-testid="blog-editor-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg">{editing === "new" ? "Artikel Baru" : "Edit Artikel"}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              <Input data-testid="blog-form-title" placeholder="Judul" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <Input data-testid="blog-form-excerpt" placeholder="Excerpt (ringkasan singkat)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm">
                  {BLOG_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input placeholder="URL cover image" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
              </div>
              <textarea data-testid="blog-form-content" placeholder="Konten (boleh HTML: <h2>, <p>, <ul>...)" rows={10}
                value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-mono" />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publikasikan
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setEditing(null)}>Batal</Button>
              <Button onClick={save} loading={saving} data-testid="blog-form-save">Simpan</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
